// frontend/modules/sms/components/SmsForm.tsx
import React from 'react';
import {
    Form,
    Input,
    Button,
    Card,
    Space,
    Divider,
    Typography,
    Badge,
    Alert
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    SendOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { useSms } from '../hooks/useSms';
import { useTheme } from '@/components/common/ThemeProvider';

const { TextArea } = Input;
const { Title, Text } = Typography;

const SmsForm: React.FC = () => {
    const { dark } = useTheme();
    const {
        message,
        recipients,
        senderId,
        reference,
        isSending,
        smsBalance,
        isLoadingBalance,

        setMessage,
        setSenderId,
        setReference,
        addRecipient,
        removeRecipient,
        updateRecipient,
        clearForm,
        sendSms,
        loadSmsBalance
    } = useSms();

    // Character count and SMS segments calculation
    const characterCount = message.length;
    const segmentSize = 160; // Standard SMS segment size
    const segmentCount = Math.ceil(characterCount / segmentSize) || 1;
    const totalSegments = segmentCount * recipients.filter(r => r.valid).length;

    // Form validation
    const isFormValid =
        message.trim() !== '' &&
        senderId.trim() !== '' &&
        recipients.some(r => r.valid);

    return (
        <Card
            title={
                <div className="flex justify-between items-center">
                    <Title level={5} className={dark ? 'text-white mb-0' : 'mb-0'}>
                        Send SMS
                    </Title>
                    <Space>
                        <Button
                            onClick={loadSmsBalance}
                            icon={<ReloadOutlined />}
                            loading={isLoadingBalance}
                        >
                            Refresh Balance
                        </Button>
                        <Badge
                            count={smsBalance !== null ? smsBalance : '...'}
                            style={{
                                backgroundColor: '#52c41a',
                                fontSize: '14px',
                                padding: '0 8px'
                            }}
                        >
                            <Text>Available SMS</Text>
                        </Badge>
                    </Space>
                </div>
            }
            className={dark ? 'bg-gray-800 text-white' : ''}
        >
            <Form layout="vertical">
                <Form.Item
                    label="Sender ID"
                    required
                    help="The name that will appear as the sender (max 11 characters)"
                >
                    <Input
                        placeholder="Your business name or brand"
                        value={senderId}
                        onChange={(e) => setSenderId(e.target.value)}
                        maxLength={11}
                        className={dark ? 'bg-gray-700 text-white border-gray-600' : ''}
                    />
                </Form.Item>

                <Form.Item
                    label="Message"
                    required
                    help={
                        <div className="flex justify-between">
                            <span>Write the message that will be sent to all recipients</span>
                            <span>
                                {characterCount} characters | {segmentCount} segment{segmentCount !== 1 ? 's' : ''}
                            </span>
                        </div>
                    }
                >
                    <TextArea
                        rows={6}
                        placeholder="Type your message here..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className={dark ? 'bg-gray-700 text-white border-gray-600' : ''}
                    />
                </Form.Item>

                <Divider orientation="left">Recipients</Divider>

                {recipients.map((recipient, index) => (
                    <Form.Item
                        key={index}
                        validateStatus={recipient.phoneNumber && !recipient.valid ? 'error' : ''}
                        help={recipient.phoneNumber && !recipient.valid ? 'Invalid phone number format. Use 255XXXXXXXXX' : ''}
                    >
                        <div className="flex gap-2">
                            <Input
                                placeholder="255XXXXXXXXX"
                                value={recipient.phoneNumber}
                                onChange={(e) => updateRecipient(index, e.target.value)}
                                className={dark ? 'bg-gray-700 text-white border-gray-600' : ''}
                            />
                            <Button
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => removeRecipient(index)}
                                disabled={recipients.length <= 1}
                            />
                        </div>
                    </Form.Item>
                ))}

                <Form.Item>
                    <Button
                        type="dashed"
                        onClick={() => addRecipient('')}
                        block
                        icon={<PlusOutlined />}
                    >
                        Add Another Recipient
                    </Button>
                </Form.Item>

                <Form.Item label="Reference (Optional)">
                    <Input
                        placeholder="Your reference ID for tracking (optional)"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        className={dark ? 'bg-gray-700 text-white border-gray-600' : ''}
                    />
                </Form.Item>

                <Divider />

                <Form.Item>
                    <div className="flex justify-between items-center">
                        <div>
                            <Text strong>Total:</Text>{' '}
                            <Badge
                                count={totalSegments}
                                style={{
                                    backgroundColor: '#1890ff',
                                    fontSize: '14px'
                                }}
                            />
                            <Text> SMS segments</Text>
                        </div>

                        <Space>
                            <Button onClick={clearForm}>
                                Clear
                            </Button>
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                onClick={sendSms}
                                loading={isSending}
                                disabled={!isFormValid}
                            >
                                Send SMS
                            </Button>
                        </Space>
                    </div>
                </Form.Item>

                {totalSegments > 0 && smsBalance !== null && totalSegments > smsBalance && (
                    <Alert
                        message="Insufficient Balance"
                        description={`You don't have enough SMS balance to send this message. Required: ${totalSegments}, Available: ${smsBalance}`}
                        type="error"
                        showIcon
                    />
                )}
            </Form>
        </Card>
    );
};

export default SmsForm;